import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {DataService} from '../../../../services/data.service';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {Budget, MarginV2} from '../../../../interfaces';
import {BudgetModel} from '../../../models/budget-model';
import {debounceTime, distinctUntilChanged, filter, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {AuthService} from '../../../../services/auth.service';
import {marginSumValidator} from './MarginSumValidator';
import {CommonModule} from '@angular/common';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSlideToggleModule} from '@angular/material/slide-toggle';
import {MatButtonModule} from '@angular/material/button';


@Component({
  selector: 'app-margin-form-v2',
  templateUrl: './margin-form-component-v2.component.html',
  styleUrls: ['./margin-form-component-v2.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSlideToggleModule,
    MatButtonModule
  ]
})
export class MarginFormV2Component implements OnInit, OnDestroy {

  public formUpdating = false;
  @Input() kind: 'ITA' | 'USA' = 'ITA';
  public marginForm: UntypedFormGroup = this.fb.group({
    generalExpenses: [12, [Validators.min(0), Validators.max(100)]],
    profit: [20, [Validators.min(0), Validators.max(100)]],
    ciTaxesPercentage: [8.875, [Validators.min(0), Validators.max(100)]],
    commission: [0, [Validators.min(0), Validators.max(100)]],
    capitalImprovement: false
  }, {validators: marginSumValidator()});
  private unsubscribe$ = new Subject();

  @Output() updateCostSummary = new EventEmitter<string>();

  private _budget: Budget;
  get budget() {
    return this._budget;
  }

  @Input() set budget(b: Budget) {
    this._budget = b;

    if (this.marginForm) {
      this.patchForm();
    } else {
      console.log('Margin form not ready yet');
    }
  }

  private canGrantProjectAuth = false;
  private canEditBudget = false;
  private canEditOtherBudget = false;

  constructor(private dataService: DataService,
              private fb: UntypedFormBuilder,
              private auth: AuthService) {
  }

  ngOnInit() {
    this.registerTaxesChange();
    this.patchForm();
    this.enableAutoSave();
    this.setPermissionHandler();
  }

  setPermissionHandler() {
    this.auth.userHasPermissions([
      'app.budget.auth.grant',
      'app.budget.write',
      'app.budget.edit.others'
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(val => {
        this.canGrantProjectAuth = val[0];
        this.canEditBudget = val[1];
        this.canEditOtherBudget = val[2];
        this.refreshFormEnabled();
      });
  }

  registerTaxesChange() {
    this.marginForm.controls['capitalImprovement'].valueChanges.subscribe(value => {
      this.checkDisableCiTax(value);
    });
  }

  areMarginsEqual(margin1: MarginV2, margin2: MarginV2): boolean {
    return margin1.generalExpenses === margin2.generalExpenses &&
      margin1.commission === margin2.commission &&
      margin1.profit === margin2.profit &&
      margin1.capitalImprovement === margin2.capitalImprovement &&
      margin1.ciTaxesPercentage === margin2.ciTaxesPercentage;
  }

  enableAutoSave() {
    this.marginForm.valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this.unsubscribe$),
        distinctUntilChanged(this.areMarginsEqual),
        filter(() => this.marginForm.valid))
      .subscribe(async () => {
        await this.submit();
      });
  }


  patchForm() {

    if (this.budget && this.budget.margin && this.marginForm) {
      const areEqual = this.areMarginsEqual((this.budget.margin as MarginV2), this.getMarginValue());
      if (!areEqual) {
        console.log('margin-form - patch form from budget as found changes: ', this.budget.margin, this.getMarginValue());
        this.marginForm.patchValue({
          generalExpenses: this.budget.margin.generalExpenses,
          ciTaxesPercentage: (this.budget.margin as MarginV2).ciTaxesPercentage,
          commission: this.budget.margin.commission,
          profit: this.budget.margin.profit,
          capitalImprovement: (this.budget.margin as MarginV2).capitalImprovement
        }, {emitEvent: false});
      }
    }
    this.refreshFormEnabled();
  }

  refreshFormEnabled() {
    if (!this.marginForm) return;

    if (BudgetModel.isMarginGenExpAndProfitEditable(
      this.budget,
      this.auth.currentUser,
      this.canGrantProjectAuth)) {
      this.marginForm.enable({emitEvent: false});
    } else {
      this.marginForm.disable({emitEvent: false});
    }

    // we use the same logic for this one
    if (BudgetModel.isRateEditable(
      this.budget,
      this.auth.currentUser,
      this.canGrantProjectAuth,
      this.canEditBudget,
      this.canEditOtherBudget)) {
      this.marginForm.get('capitalImprovement').enable({emitEvent: false});
      this.marginForm.get('ciTaxesPercentage').enable({emitEvent: false});
      this.marginForm.get('commission').enable({emitEvent: false});
    } else {
      this.marginForm.get('capitalImprovement').disable({emitEvent: false});
      this.marginForm.get('ciTaxesPercentage').disable({emitEvent: false});
      this.marginForm.get('commission').disable({emitEvent: false});
    }
  }


  checkDisableCiTax(enabled: boolean) {
    if (enabled) {
      this.marginForm.controls['ciTaxesPercentage'].enable({emitEvent: false});
    } else {
      this.marginForm.controls['ciTaxesPercentage'].disable({emitEvent: false});
    }
  }

  async updateBudget(updateObj: { margin: MarginV2 }) {
    console.log('margin-form: updating budget margin...', updateObj.margin);
    this.formUpdating = true;
    this.marginForm.disable({emitEvent: false});

    try {
      await this.dataService.patchBudget(this._budget.id, updateObj);
    } catch (err) {
      console.log(err);
      this.checkDisableCiTax(this.marginForm.controls['capitalImprovement'].value);
    } finally {
      this.checkDisableCiTax(this.marginForm.controls['capitalImprovement'].value);
      this.formUpdating = false;
      this.marginForm.enable({emitEvent: false});
    }
  }

  getMarginValue()
    :
    MarginV2 {
    return {
      generalExpenses: this.marginForm.getRawValue().generalExpenses,
      ciTaxesPercentage: this.marginForm.getRawValue().ciTaxesPercentage,
      commission: this.marginForm.value.commission,
      profit: this.marginForm.getRawValue().profit,
      capitalImprovement: this.marginForm.getRawValue().capitalImprovement
    };
  }

  async submit() {
    const margin = this.getMarginValue();
    console.log('Submitting margin:', margin);
    await this.updateBudget({margin});
    this.updateCostSummary.emit(this._budget.id);
  }

  public ngOnDestroy() {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
